"use client";

import { ArrowUpRight, Blocks, BrainCircuit, Lock } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
export default function Productshowcase() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const pillars = [{
    number: "01",
    title: "GLOBAL SOURCING",
    icon: Blocks,
    description: "We bridge the gap between world-class medical manufacturers and local healthcare providers. Our team meticulously evaluates innovative technologies, ensuring that only the most reliable and clinically valuable devices enter our distribution network."
  }, {
    number: "02",
    title: "CLINICAL EVALUATION",
    icon: BrainCircuit,
    description: "Our commitment to excellence is grounded in rigorous clinical assessment. We partner with medical experts to validate product efficacy, safety, and long-term performance, providing our clients with the confidence to adopt cutting-edge solutions."
  }, {
    number: "03",
    title: "REGULATORY SUPPORT",
    icon: Lock,
    description: "Navigating international medical regulations is complex. We provide comprehensive regulatory guidance and compliance support, ensuring that every device we distribute meets the highest global standards for patient safety and operational integrity."
  }];
  return <section data-section-id="5200" id="productshowcase" className="bg-background text-foreground py-32 md:py-50 px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-16 md:mb-24 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-4">
            <div className="flex flex-col gap-3">
              <span className="font-default text-xs md:text-sm tracking-widest uppercase text-blue-600">
                02 / OUR CORE VALUES
              </span>
              <div className="h-px w-16 bg-blue-600" />
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-default font-bold text-4xl sm:text-5xl md:text-6xl lg:text-[88px] leading-[0.95] tracking-tight uppercase">
              THREE /
              <br />
              STRATEGIC PILLARS.
            </h2>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <PillarCard key={i} pillar={pillar} Icon={Icon} index={i} />;
        })}
        </div>
      </div>
    </section>;
}
function PillarCard({
  pillar,
  Icon,
  index
}: {
  pillar: {
    number: string;
    title: string;
    description: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} style={{
    transitionDelay: `${index * 100}ms`
  }} className={`transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
      <Card className="group relative h-full min-h-[480px] bg-slate-50 backdrop-blur-sm border border-slate-200 rounded-lg p-10 md:p-12 flex flex-col shadow-md hover:border-blue-600 hover:shadow-lg transition-all duration-500">
        {}
        <div className="font-default text-5xl md:text-6xl font-light text-blue-600 transition-transform duration-500 group-hover:scale-110 origin-left">
          {pillar.number}
        </div>

        {}
        <div className="h-px w-full bg-blue-200 mt-6 mb-10 group-hover:bg-blue-600 transition-colors duration-500" />

        {}
        <Icon className="size-8 text-slate-600 group-hover:text-blue-600 transition-colors duration-500 mb-8" strokeWidth={1} />

        {}
        <h3 className="font-default font-bold text-2xl md:text-3xl uppercase tracking-tight mb-6 leading-tight">
          {pillar.title}
        </h3>

        {}
        <p className="font-default text-base md:text-[17px] leading-relaxed text-slate-700 mb-10 flex-grow">
          {pillar.description}
        </p>

        {}
        <div className="mt-auto">
          <Button variant="ghost" size="sm" className="font-default text-xs tracking-widest uppercase text-slate-900 hover:text-blue-600 hover:bg-transparent p-0 h-auto group/btn">
            LEARN MORE
            <ArrowUpRight className="size-4 text-blue-600 transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
          </Button>
        </div>
      </Card>
    </div>;
}